import React from 'react';
import classNames from 'classnames';
import track from 'react-tracking';
import { ChildProps, MutationFn } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

import { withRouter, WithRouterProps } from 'react-router';
import gql from 'graphql-tag';

import { PageSettings } from './map_params';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import Tiles from '@reverbdotcom/commons/src/components/tiles';
import ListingCard from '@reverbdotcom/commons/src/components/listing_card';
import { trackEvent, LISTING_TYPE } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { GridEmptyState } from './listing_grid';
import {
  CombinedMarketplaceBumpTracking,
  CoreMarketplaceCombinedMarketplaceSearch,
  Ad,
} from '../../schemas/rql';
import ErrorDisplay from '../discovery/error_display';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import * as elog from '@reverbdotcom/commons/src/elog';
import InterspersedListings from './interspersed_listings';
import { connect } from '@reverbdotcom/commons/src/connect';
import { RCListingGrid } from '@reverbdotcom/cadence/components';
import { ThemeType } from '@reverbdotcom/cadence/components/types';
import { ThemeContext } from '@reverbdotcom/commons/src/components/theme_context_provider';

interface MutationProps {
  mutate: MutationFn<CombinedMarketplaceBumpTracking.Mutation, CombinedMarketplaceBumpTracking.Variables>;
}

interface IExternalProps {
  listings: CoreMarketplaceCombinedMarketplaceSearch.Listings[];
  bumpedListings: CoreMarketplaceCombinedMarketplaceSearch.Listings[];
  error: Error;
  loading: boolean;
  trackingQuery?: string;
  ad?: Ad;
  pageSettings?: PageSettings;
}

type IProps = ChildProps<
  IExternalProps & WithRouterProps & IUserContext & MutationProps,
  CoreMarketplaceCombinedMarketplaceSearch.Query,
  CoreMarketplaceCombinedMarketplaceSearch.Variables
>;

const COMPONENT_NAME = 'CombinedMarketplaceGrid';

export class CombinedBumpListingGrid extends React.Component<IProps, null> {
  static contextType = ThemeContext;

  shouldDisplayBumps() {
    if (this.props.loading || this.props.error || !this.props.bumpedListings.length) {
      return false;
    }

    return true;
  }

  listingIds() {
    return this.props.listings.map(l => l.id);
  }

  bumpedListingIds() {
    return this.props.bumpedListings.map(l => l.id);
  }

  componentDidUpdate(prevProps) {
    if (
      this.shouldDisplayBumps() &&
      prevProps.bumpedListings !== this.props.bumpedListings
    ) {
      this.trackListingBumpImpressions(this.bumpedListingIds());

      trackEvent({
        eventName: MParticleEventName.ListingImpression,
        listingIds: this.bumpedListingIds(),
        shownAsBumped: true,
        listings: this.props.listings,
        componentName: COMPONENT_NAME,
      });
    }
  }

  trackListingBumpImpressions(listingIds: string[]) {
    return this.props.mutate({
      variables: {
        input: {
          listingIds,
        },
      },
    });
  }

  render() {
    if (this.props.error) {
      elog.error('listing-grid-error', { ...this.props }, this.props.error);
      return <ErrorDisplay />;
    }

    if (this.props.loading && !this.props.listings.length) {
      return (
        <Tiles
          loading={this.props.loading}
          placeholder={<ListingCard listing={null} />}
          expectedCount={10}
          emptyState={GridEmptyState()}
        />
      );
    }

    if (this.props.bumpedListings.length === 0 && this.props.listings.length === 0) {
      return GridEmptyState();
    }

    const classes = classNames(
      'sortable-tiles',
      { 'sortable-tiles--reloading': this.props.listings.length && this.props.loading },
    );

    const useRCListingGrid = this.context?.theme === ThemeType.web2;

    const interspersedListings = (
      <InterspersedListings
        user={this.props.user}
        listings={this.props.listings}
        bumpedListings={this.props.bumpedListings}
        showOnlySold={this.props.location.query.showOnlySold}
        trackingQuery={this.props.trackingQuery}
        ad={this.props.ad}
        pageSettings={this.props.pageSettings}
        useRCListingGrid={useRCListingGrid}
      />
    );

    if (useRCListingGrid) {
      return (
        <RCListingGrid
          loading={this.props.loading}
        >
          {interspersedListings}
        </RCListingGrid>
      );
    }

    return (
      <ul className={classes}>
        {interspersedListings}
      </ul>
    );
  }
}

const withMutation = withGraphql<IExternalProps, CombinedMarketplaceBumpTracking.Mutation, CombinedMarketplaceBumpTracking.Variables>(
  gql`
  mutation CombinedMarketplaceBumpTracking($input: Input_arbiter_BulkTrackImpressionsRequest) {
    bulkTrackImpressions(input: $input) {
      listingIds
    }
  }
  `,
);

export default connect<IExternalProps>([
  withUserContext,
  withRouter,
  withMutation,
  track({
    componentName: COMPONENT_NAME,
    type: LISTING_TYPE,
  }),
])(CombinedBumpListingGrid);
